<template>
  <div>
    <div class="text-lg font-extrabold tracking-tight">Налаштування · Безпека</div>
    <div class="mt-1 text-sm text-white/65">Керуйте паролем свого акаунта.</div>

    <div class="mt-6">
      <div class="acc-glass rounded-2xl p-5 border border-white/10 w-full">
        <h2 id="change-password-heading" class="text-base font-extrabold">Змінити пароль</h2>

        <form class="mt-5 space-y-4" aria-labelledby="change-password-heading" novalidate @submit.prevent="save">
          <div>
            <label for="settings-current-password" class="mb-2 block text-sm font-semibold text-white/85">
              Поточний пароль
            </label>
            <div class="relative">
              <input
                id="settings-current-password"
                v-model="form.current_password"
                :type="passwordVisible.current_password ? 'text' : 'password'"
                autocomplete="current-password"
                class="foxy-input min-h-11 !pr-12 text-sm"
                :class="fieldErrors.current_password ? '!border-foxy-error focus:!border-foxy-error focus:!ring-foxy-error/20' : ''"
                :aria-invalid="fieldErrors.current_password ? 'true' : 'false'"
                :aria-describedby="fieldErrors.current_password ? 'settings-current-password-error' : null"
              >
              <button
                type="button"
                class="foxy-auth-eye w-11 justify-center !pr-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action focus-visible:ring-offset-2 focus-visible:ring-offset-foxy-surface"
                :aria-label="toggleLabel('current_password')"
                :aria-pressed="passwordVisible.current_password ? 'true' : 'false'"
                @click="togglePassword('current_password')"
              >
                <i :class="passwordVisible.current_password ? 'fas fa-eye-slash' : 'fas fa-eye'" aria-hidden="true"></i>
              </button>
            </div>
            <p v-if="fieldErrors.current_password" id="settings-current-password-error" class="mt-1 text-sm text-foxy-error dark:text-red-200" role="alert">
              {{ fieldErrors.current_password }}
            </p>
          </div>

          <div>
            <label for="settings-new-password" class="mb-2 block text-sm font-semibold text-white/85">
              Новий пароль
            </label>
            <div class="relative">
              <input
                id="settings-new-password"
                v-model="form.password"
                :type="passwordVisible.password ? 'text' : 'password'"
                autocomplete="new-password"
                class="foxy-input min-h-11 !pr-12 text-sm"
                :class="fieldErrors.password ? '!border-foxy-error focus:!border-foxy-error focus:!ring-foxy-error/20' : ''"
                :aria-invalid="fieldErrors.password ? 'true' : 'false'"
                :aria-describedby="passwordDescription"
              >
              <button
                type="button"
                class="foxy-auth-eye w-11 justify-center !pr-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action focus-visible:ring-offset-2 focus-visible:ring-offset-foxy-surface"
                :aria-label="toggleLabel('password')"
                :aria-pressed="passwordVisible.password ? 'true' : 'false'"
                @click="togglePassword('password')"
              >
                <i :class="passwordVisible.password ? 'fas fa-eye-slash' : 'fas fa-eye'" aria-hidden="true"></i>
              </button>
            </div>
            <p id="settings-new-password-help" class="mt-1 text-sm text-white/60">
              Щонайменше 8 символів. Довший пароль або фраза буде надійнішим.
            </p>
            <p v-if="fieldErrors.password" id="settings-new-password-error" class="mt-1 text-sm text-foxy-error dark:text-red-200" role="alert">
              {{ fieldErrors.password }}
            </p>
          </div>

          <div>
            <label for="settings-password-confirmation" class="mb-2 block text-sm font-semibold text-white/85">
              Повторіть новий пароль
            </label>
            <div class="relative">
              <input
                id="settings-password-confirmation"
                v-model="form.password_confirmation"
                :type="passwordVisible.password_confirmation ? 'text' : 'password'"
                autocomplete="new-password"
                class="foxy-input min-h-11 !pr-12 text-sm"
                :class="fieldErrors.password_confirmation ? '!border-foxy-error focus:!border-foxy-error focus:!ring-foxy-error/20' : ''"
                :aria-invalid="fieldErrors.password_confirmation ? 'true' : 'false'"
                :aria-describedby="fieldErrors.password_confirmation ? 'settings-password-confirmation-error' : null"
              >
              <button
                type="button"
                class="foxy-auth-eye w-11 justify-center !pr-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action focus-visible:ring-offset-2 focus-visible:ring-offset-foxy-surface"
                :aria-label="toggleLabel('password_confirmation')"
                :aria-pressed="passwordVisible.password_confirmation ? 'true' : 'false'"
                @click="togglePassword('password_confirmation')"
              >
                <i :class="passwordVisible.password_confirmation ? 'fas fa-eye-slash' : 'fas fa-eye'" aria-hidden="true"></i>
              </button>
            </div>
            <p v-if="fieldErrors.password_confirmation" id="settings-password-confirmation-error" class="mt-1 text-sm text-foxy-error dark:text-red-200" role="alert">
              {{ fieldErrors.password_confirmation }}
            </p>
          </div>

          <button type="submit" class="acc-btn-primary min-h-11 w-full justify-center" :disabled="saving">
            {{ saving ? 'Зміна пароля...' : 'Змінити пароль' }}
          </button>

          <p v-if="message" class="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-700 dark:text-emerald-200" role="status" aria-live="polite">
            {{ message }}
          </p>
          <p v-if="error" class="rounded-xl border border-foxy-error/30 bg-foxy-error/10 p-3 text-sm text-foxy-error dark:text-red-200" role="alert" aria-live="assertive">
            {{ error }}
          </p>
        </form>

        <div class="mt-5 border-t border-white/10 pt-4 text-sm">
          <p class="text-white/65">Не пам’ятаєте поточний пароль?</p>
          <a href="/forgot-password" class="mt-1 inline-flex min-h-11 items-center font-semibold text-foxy-action underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action focus-visible:ring-offset-2 focus-visible:ring-offset-foxy-surface">
            Скинути пароль через email
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="js">
import axios from 'axios';

export default {
  name: 'SettingsSecurity',
  data() {
    return {
      form: {
        current_password: '',
        password: '',
        password_confirmation: '',
      },
      passwordVisible: {
        current_password: false,
        password: false,
        password_confirmation: false,
      },
      fieldErrors: {},
      saving: false,
      message: '',
      error: '',
    };
  },
  computed: {
    passwordDescription() {
      return this.fieldErrors.password
        ? 'settings-new-password-help settings-new-password-error'
        : 'settings-new-password-help';
    },
  },
  methods: {
    togglePassword(field) {
      this.passwordVisible[field] = !this.passwordVisible[field];
    },
    toggleLabel(field) {
      return this.passwordVisible[field] ? 'Сховати пароль' : 'Показати пароль';
    },
    firstFieldErrors(errors) {
      return Object.fromEntries(
        Object.entries(errors || {}).map(([field, messages]) => [
          field,
          Array.isArray(messages) ? String(messages[0] || '') : String(messages || ''),
        ]),
      );
    },
    async save() {
      if (this.saving) return;

      this.saving = true;
      this.message = '';
      this.error = '';
      this.fieldErrors = {};
      try {
        await axios.put('/api/account/profile/password', this.form);
        this.message = 'Пароль змінено.';
        this.form.current_password = '';
        this.form.password = '';
        this.form.password_confirmation = '';
        this.passwordVisible.current_password = false;
        this.passwordVisible.password = false;
        this.passwordVisible.password_confirmation = false;
      } catch (e) {
        const status = e?.response?.status;
        const errors = this.firstFieldErrors(e?.response?.data?.errors);
        this.fieldErrors = errors;

        if (status === 429) {
          this.error = 'Забагато спроб. Спробуйте трохи пізніше.';
        } else if (!Object.keys(errors).length) {
          this.error = 'Не вдалося змінити пароль. Спробуйте ще раз.';
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
